import { ErrorContent, Loading, Table } from '@/components';
import { useUsersWithUniqueOrganization } from '@/features/tenant-manager/hooks';
import { NEST_ITEM_CONNECTOR, currentFiscalYear } from '@/utils';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

const PAGE_SIZE = 5;

type Props = {
  organizationIds: string[] | undefined;
  pageNumber: number;
  referenceFiscalYear?: string;
  userName: string | null | undefined;
  setPageNumber: (pageNumber: number) => void;
};

export const SearchUsersTable: FC<Props> = ({
  organizationIds,
  pageNumber,
  referenceFiscalYear = currentFiscalYear(),
  userName,
  setPageNumber,
}) => {
  const { t } = useTranslation();

  const {
    data: users,
    isLoading: isUsersLoading,
    isError: isUsersError,
  } = useUsersWithUniqueOrganization({
    params: {
      page: pageNumber,
      pageSize: PAGE_SIZE,
      name: userName,
      organizationIds,
      referenceFiscalYear,
    },
  });

  if (isUsersError) {
    return <ErrorContent reloadable={false} height="300px" />;
  }

  if (isUsersLoading) {
    return <Loading />;
  }

  return (
    <Table
      data-dd-privacy="mask"
      enableSticky={false}
      dataSource={users.results}
      tableLayout="fixed"
      rowKey={(record) =>
        `${record.id}${record.organizationStructures.at(-1)?.id}`
      }
      scroll={{ y: 200 }}
      pagination={{
        showSizeChanger: false,
        total: users.count,
        pageSize: PAGE_SIZE,
        current: pageNumber,
        onChange: (page) => {
          setPageNumber(page);
        },
      }}
      columns={[
        {
          title: t('user-name'),
          dataIndex: 'name',
        },
        {
          title: t('email'),
          dataIndex: 'email',
        },
        {
          title: t('organization-name'),
          render: (_, record) => {
            const result = record.organizationStructures
              .map((organizationStructure) => organizationStructure.name)
              .join(NEST_ITEM_CONNECTOR);

            return result;
          },
        },
      ]}
    />
  );
};
